import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Trophy, Crown, Medal, FilePdf } from '@phosphor-icons/react';

/**
 * Public Season Standings (v220).
 *
 * Read-only, no-auth version of `OrgPointsAdmin`'s standings pane. Anyone
 * with the org's URL can view the leaderboard, per-class/per-division
 * tabs, and download the printable PDF. No member PII (email/phone) is
 * ever surfaced by the backend endpoint powering this page.
 */
export default function OrgStandingsPublic() {
  const { orgId } = useParams();
  const [standings, setStandings] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = year ? `?year=${year}` : '';
      const r = await axios.get(`${BACKEND_URL}/api/organizations/${orgId}/public-standings${q}`);
      setStandings(r.data);
      const buckets = r.data.buckets || [];
      const firstWithData = buckets.find((b) => (b.standings || []).length > 0);
      setSelectedBucket((firstWithData || buckets[0])?.division || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load standings');
    } finally { setLoading(false); }
  }, [orgId, year, BACKEND_URL]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = () => {
    const q = year ? `?year=${year}` : '';
    // Public PDF endpoint — no auth headers needed, plain window navigation.
    window.open(`${BACKEND_URL}/api/organizations/${orgId}/public-standings.pdf${q}`, '_blank');
  };

  const buckets = standings?.buckets || [];
  const activeBucket = buckets.find((b) => b.division === selectedBucket) || buckets[0];
  const activeRows = activeBucket?.standings || [];
  const activeDivName = activeBucket?.division || '';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-6" data-testid="public-standings-page">
      <Link to="/shows" className="inline-flex items-center gap-2 text-[#9E9E9E] hover:text-white text-sm mb-6" data-testid="back-to-shows">
        <ArrowLeft size={16} /> Back to shows
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1 flex items-center gap-2" style={{fontFamily: 'Outfit'}}>
          <Trophy size={26} weight="fill" className="text-[#FF9500]" />
          {standings?.organization_name || 'Season Standings'}
        </h1>
        <p className="text-[#9E9E9E] text-sm">
          Season standings · Shows counted: <span className="text-white font-semibold">{standings?.shows_counted ?? 0}</span>
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          type="number"
          placeholder="Year (optional)"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm w-32"
          data-testid="public-standings-year"
        />
        <button
          onClick={load}
          className="px-3 py-1.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded text-xs font-bold hover:bg-[#FF9500]/25"
          data-testid="public-standings-refresh"
        >
          Refresh
        </button>
        {standings && (
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-bold hover:bg-[#0096FF]/25"
            data-testid="public-standings-pdf-btn"
          >
            <FilePdf size={13} weight="bold" /> Download PDF
          </button>
        )}
      </div>

      {loading && <p className="text-[#555] text-sm text-center py-8">Loading…</p>}

      {!loading && standings && (
        <section className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6" data-testid="public-standings-section">
          <p className="text-[#9E9E9E] text-xs mb-3">
            Format: <span className="text-[#FF9500] font-semibold">{standings.division_format}</span>
          </p>
          {/* Tabs: one per division (1D, 2D, 3D, 4D...) based on org format. */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1 border-b border-[#262626]" data-testid="public-standings-tabs">
            {buckets.map((b) => (
              <button
                key={b.division}
                onClick={() => setSelectedBucket(b.division)}
                className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-t border-b-2 transition-colors whitespace-nowrap ${
                  (selectedBucket || buckets[0]?.division) === b.division
                    ? 'text-[#FF9500] border-[#FF9500] bg-[#FF9500]/5'
                    : 'text-[#9E9E9E] border-transparent hover:text-white'
                }`}
                data-testid={`public-standings-tab-${b.division}`}
              >
                {b.division === 'OVERALL' ? 'Overall' : b.division}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#9E9E9E] text-[10px] uppercase tracking-wider border-b border-[#262626]">
                  <th className="text-left py-2">Rank</th>
                  <th className="text-left py-2">Rider</th>
                  <th className="text-right py-2">Points</th>
                  <th className="text-right py-2">Placings</th>
                  <th className="text-right py-2">Award</th>
                </tr>
              </thead>
              <tbody>
                {activeRows.map((s, i) => {
                  const rank = i + 1;
                  const isChamp = rank === 1;
                  const isReserve = rank === 2;
                  return (
                    <tr
                      key={s.member_id}
                      className={`border-b border-[#262626]/50 ${
                        isChamp ? 'bg-[#FFD700]/10' : isReserve ? 'bg-[#C0C0C0]/10' : ''
                      }`}
                      data-testid={`public-standing-row-${s.member_id}`}
                    >
                      <td className="py-2 text-[#9E9E9E]">#{rank}</td>
                      <td className="py-2 font-semibold">{s.first_name} {s.last_name}</td>
                      <td className="py-2 text-right text-[#FF9500] font-bold">{s.total_points}</td>
                      <td className="py-2 text-right text-[#9E9E9E] text-xs">{s.results?.length ?? 0}</td>
                      <td className="py-2 text-right">
                        {isChamp && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 rounded text-[10px] font-bold uppercase tracking-wider">
                            <Crown size={11} weight="fill" /> {activeDivName} Champ
                          </span>
                        )}
                        {isReserve && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C0C0C0]/20 text-[#D4D4D4] border border-[#C0C0C0]/40 rounded text-[10px] font-bold uppercase tracking-wider">
                            <Medal size={11} weight="fill" /> {activeDivName} Reserve
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {activeRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-[#555] py-8">
                      No qualifying results in the {activeDivName} yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
